import React, { useMemo } from "react";
import { useDispatch } from "react-redux";

import { Skeleton, Table } from "components";
import { history } from "helpers";
import { useLoader } from "hooks";
import {
	useChangeFeeTemplateStatusMutation,
	useCopyFeeTemplateMutation,
	useDeleteFeeTemplateMutation,
	useUpdateFeeTemplateDefaultMutation,
} from "queries";
import { uiActions } from "store";
import { ColumnTypes } from "types";

import * as table from "./tableColumns";

type FeeTableProps = {
	companyId: string;
	editPageName: string;
	useFeeTemplateQueueQuery: Function;
	useCheckFeeTemplateNameMutation: Function;
	deleteFeeTemplate: Function;
};

const FeeTemplateQueueTable = ({
	companyId,
	editPageName,
	useFeeTemplateQueueQuery,
	useCheckFeeTemplateNameMutation,
	deleteFeeTemplate,
}: FeeTableProps) => {
	const {
		data,
		isLoading: isLoadingFeeTemplate,
		refetch,
	}: {
		data;
		isLoading: boolean;
		refetch: Function;
	} = useFeeTemplateQueueQuery({
		companyId,
	});

	const dispatch = useDispatch();

	const { mutate: setDefault, isLoading: isLoadingUpdateDefault } =
		useUpdateFeeTemplateDefaultMutation(refetch);

	const { mutate: setStatus, isLoading: isLoadingSetStatus } =
		useChangeFeeTemplateStatusMutation(refetch);

	const handleSetDefault = (templateId, isDefault) => {
		setDefault({ companyId, templateId, isDefault });
	};

	const handleSetStatus = (templateId, active) => {
		setStatus({ companyId, templateId, active });
	};

	const { mutateAsync: copyTemplate, isLoading: isLoadingCopy } =
		useCopyFeeTemplateMutation(refetch);

	const handleCopy = templateId => {
		dispatch(
			uiActions.showModal("COPY_TEMPLATE", {
				async onSubmit({ name }) {
					copyTemplate({ companyId, templateId, name });
				},
				companyId,
				useCheckFeeTemplateNameMutation,
			}),
		);
	};

	const handleEdit = templateId => {
		history.push(
			`/system-setup/agent-office-setup/edit-agent-office/${companyId}/${editPageName}/${templateId}`,
		);
	};

	const { mutateAsync: deleteTemplate, isLoading: isLoadingDelete } =
		useDeleteFeeTemplateMutation(deleteFeeTemplate, refetch);

	const handleDelete = templateId => {
		dispatch(
			uiActions.showModal("CONFIRMATION", {
				title: [
					"Are you sure you want to delete this item?",
					"You won't be able to undo.",
				],
				async onConfirm() {
					try {
						dispatch(uiActions.showSpinnerOverlay());
						await deleteTemplate({ companyId, templateId });
						refetch();
					} finally {
						dispatch(uiActions.hideSpinnerOverlay());
					}
				},
			}),
		);
	};

	const isLoading =
		isLoadingFeeTemplate ||
		isLoadingUpdateDefault ||
		isLoadingSetStatus ||
		isLoadingDelete ||
		isLoadingCopy;

	const tableColumns: ColumnTypes = [
		table.NameColumn(),
		table.ActionSwitchColumn({
			handleSetDefault,
			handleSetStatus,
			isLoading: isLoading,
		}),
		table.ActionColumn({
			handleCopy,
			handleEdit,
			handleDelete,
			isLoading: isLoading,
		}),
	];

	const meta = useMemo(
		() => ({
			loading: isLoadingFeeTemplate,
			totalItems: data?.length,
		}),
		[data?.length, isLoadingFeeTemplate],
	);

	const Loader = useLoader(isLoadingFeeTemplate);

	return (
		<Loader
			skeleton={<Skeleton height="150px" />}
			component={
				<Table
					columns={tableColumns}
					data={data}
					meta={meta}
					disableHeader
					hideFooter
				/>
			}
		/>
	);
};

export default FeeTemplateQueueTable;
